import { formatTransactionNaira } from '../utils/transactionMoney';
import React, { useCallback, useEffect, useState } from "react";
import {
Box,
Card,
Typography,
TextField,
MenuItem,
Chip,
Tabs,
Tab,
Stack,
Pagination,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
Alert,
Checkbox,
FormControlLabel,
CircularProgress,
Tooltip
} from "@mui/material";

import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PaidIcon from "@mui/icons-material/Paid";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useNavigate } from "react-router-dom";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { formatAdminDateTime } from "../utils/adminDateTime";
import TableLoadingOverlay from "../components/TableLoadingOverlay";
import { useSelector } from "react-redux";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import AdminPageContainer from "../components/AdminPageContainer";
import {
  adminPageSx,
  contentCardSx,
  filterStackSx,
  metricCardSx,
  metricGridSx,
  paginationBarSx
} from "../utils/adminResponsive";

const transactionFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  background: "#fff",
  borderRadius: 1,
  "& .MuiInputBase-root": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

const noWrapDesktopCellSx = {
  whiteSpace: "nowrap",
  wordBreak: "normal",
  overflowWrap: "normal"
};

const transactionHeadCellSx = {
  ...noWrapDesktopCellSx,
  fontWeight: 700
};

export default function Transactions() {
  const navigate = useNavigate();
  const adminRole = useSelector((state) => state.auth.admin);
  const isSuperAdmin = adminRole === "super_admin";
  const canViewDrawSummary = adminRole === "super_admin" || adminRole === "finance_admin";
  const [transactions, setTransactions] = useState([]);
  const [referenceMatches, setReferenceMatches] = useState([]);
  const [drawSummaries, setDrawSummaries] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [statisticsError, setStatisticsError] = useState('');
  const updateStatistics = useCallback((value, error) => { setStats(value); setStatisticsError(error || (value ? '' : 'Transaction statistics unavailable.')); }, []);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [recordTypeFilter, setRecordTypeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("transactions");
  const [transactionPage, setTransactionPage] = useState(1);
  const [summaryPage, setSummaryPage] = useState(1);
  const [paymentDetail, setPaymentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualPassword, setManualPassword] = useState("");
  const [manualReason, setManualReason] = useState("");
  const [manualConfirmed, setManualConfirmed] = useState(false);
  const [closureOpen, setClosureOpen] = useState(false);
  const [closurePassword, setClosurePassword] = useState("");
  const [closureReason, setClosureReason] = useState("");
  const [closureConfirmed, setClosureConfirmed] = useState(false);
  const [resolvingPaymentId, setResolvingPaymentId] = useState(null);
  const [resolutionMessage, setResolutionMessage] = useState("");
  const [bulkResolveOpen, setBulkResolveOpen] = useState(false);
  const [bulkResolvePassword, setBulkResolvePassword] = useState("");
  const [bulkResolveConfirmed, setBulkResolveConfirmed] = useState(false);
  const [bulkResolving, setBulkResolving] = useState(false);
  const [bulkResolveError, setBulkResolveError] = useState("");
  const [bulkResolveResult, setBulkResolveResult] = useState(null);
  const rowsPerPage = 20;

  const openPaymentDetail = async (transaction) => {
    if (!isSuperAdmin || transaction.record_type !== "payment" || !transaction.reconciliation_eligible) return;
    setPaymentDetail({ id: transaction.record_id });
    setDetailLoading(true);
    setDetailError("");
    setAdminPassword("");
    setManualOpen(false);
    setClosureOpen(false);
    try {
      const response = await adminFetch(`/api/admin/transactions/payments/${transaction.record_id}/reconciliation`);
      if (!response?.status) throw new Error(response?.message || "Unable to load payment details");
      setPaymentDetail(response.data);
    } catch (error) {
      setDetailError(error.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const closePaymentDetail = () => {
    if (reconciling) return;
    setPaymentDetail(null);
    setDetailError("");
    setAdminPassword("");
  };

  const closeClosureDialog = () => {
    if (reconciling) return;
    setClosureOpen(false);
    setClosurePassword("");
    setClosureReason("");
    setClosureConfirmed(false);
  };

  const reconcilePayment = async () => {
    if (!paymentDetail?.id || !adminPassword || reconciling) return;
    setReconciling(true);
    setDetailError("");
    try {
      const response = await adminFetch(`/api/admin/transactions/payments/${paymentDetail.id}/reconciliation`, {
        method: "POST",
        body: JSON.stringify({ confirm: true, password: adminPassword })
      });
      if (!response?.status) throw new Error(response?.message || "Reconciliation failed");
      setAdminPassword("");
      const refreshed = await adminFetch(`/api/admin/transactions/payments/${paymentDetail.id}/reconciliation`);
      if (refreshed?.status) setPaymentDetail(refreshed.data);
      const list = await adminFetch("/api/admin/transactions");
      if (list?.status) { setTransactions(list.data.transactions || []); updateStatistics(list.data.stats || null, list.data.stats_error); }
      setDetailError(response.message);
    } catch (error) {
      setDetailError(error.message);
    } finally {
      setReconciling(false);
    }
  };

  const manuallyConfirmPayment = async () => {
    if (!paymentDetail?.id || !manualPassword || manualReason.trim().length < 10 || !manualConfirmed || reconciling) return;
    setReconciling(true);
    setDetailError("");
    try {
      const response = await adminFetch(`/api/admin/transactions/payments/${paymentDetail.id}/manual-confirmation`, {
        method: "POST", body: JSON.stringify({ confirm: true, password: manualPassword, reason: manualReason.trim() })
      });
      if (!response?.status) throw new Error(response?.message || "Manual confirmation failed");
      setManualOpen(false);
      setManualPassword(""); setManualReason(""); setManualConfirmed(false);
      const refreshed = await adminFetch(`/api/admin/transactions/payments/${paymentDetail.id}/reconciliation`);
      if (refreshed?.status) setPaymentDetail(refreshed.data);
      const list = await adminFetch("/api/admin/transactions");
      if (list?.status) { setTransactions(list.data.transactions || []); updateStatistics(list.data.stats || null, list.data.stats_error); }
      setDetailError(response.message);
    } catch (error) { setDetailError(error.message); }
    finally { setReconciling(false); }
  };

  const closeTransaction = async () => {
    if (!paymentDetail?.id || !closurePassword || closureReason.trim().length < 10 ||
        !closureConfirmed || reconciling) return;
    setReconciling(true);
    setDetailError("");
    try {
      const response = await adminFetch(`/api/admin/transactions/payments/${paymentDetail.id}/closure`, {
        method: "POST", body: JSON.stringify({ confirm: true, password: closurePassword, reason: closureReason.trim() })
      });
      if (!response?.status) throw new Error(response?.message || "Unable to close transaction");
      setClosureOpen(false); setClosurePassword(""); setClosureReason(""); setClosureConfirmed(false);
      const [detail, list] = await Promise.all([
        adminFetch(`/api/admin/transactions/payments/${paymentDetail.id}/reconciliation`),
        adminFetch("/api/admin/transactions")
      ]);
      if (detail?.status) setPaymentDetail(detail.data);
      if (list?.status) { setTransactions(list.data.transactions || []); updateStatistics(list.data.stats || null, list.data.stats_error); }
      setDetailError(response.message);
    } catch (error) { setDetailError(error.message); }
    finally { setReconciling(false); }
  };

  const resolvePendingPayment = async (transaction) => {
    if (!isSuperAdmin || resolvingPaymentId || transaction.status !== "pending" ||
        transaction.record_type !== "payment" || !transaction.reconciliation_eligible) return;
    setResolvingPaymentId(transaction.record_id);
    setResolutionMessage("Checking transaction with Paystack...");
    try {
      const response = await adminFetch(`/api/admin/transactions/payments/${transaction.record_id}/resolve`, {
        method: "POST"
      });
      if (!response?.status) throw new Error(response?.message || "Unable to resolve payment");
      setResolutionMessage(response.message);
      const list = await adminFetch("/api/admin/transactions");
      if (list?.status) { setTransactions(list.data.transactions || []); updateStatistics(list.data.stats || null, list.data.stats_error); }
    } catch (error) {
      setResolutionMessage(error.message);
    } finally {
      setResolvingPaymentId(null);
    }
  };

  const closeBulkResolveDialog = () => {
    if (bulkResolving) return;
    setBulkResolveOpen(false);
    setBulkResolvePassword("");
    setBulkResolveConfirmed(false);
    setBulkResolveError("");
    setBulkResolveResult(null);
  };

  const resolveAllPendingPayments = async () => {
    if (!isSuperAdmin || bulkResolving || !bulkResolvePassword || !bulkResolveConfirmed) return;
    setBulkResolving(true);
    setBulkResolveError("");
    setBulkResolveResult(null);
    try {
      const response = await adminFetch("/api/admin/transactions/payments/resolve-pending", {
        method: "POST",
        body: JSON.stringify({ confirm: true, password: bulkResolvePassword })
      });
      if (!response?.status) throw new Error(response?.message || "Unable to resolve pending payments");
      setBulkResolvePassword("");
      setBulkResolveConfirmed(false);
      const jobId = response.data?.job_id;
      if (!jobId) throw new Error("Bulk resolution did not return a job identifier");
      let job;
      do {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const statusResponse = await adminFetch(`/api/admin/transactions/payments/resolve-pending/${encodeURIComponent(jobId)}`);
        if (!statusResponse?.status) throw new Error(statusResponse?.message || "Unable to check bulk resolution progress");
        job = statusResponse.data;
        setBulkResolveResult(job);
      } while (job.status === "running");
      if (job.status !== "completed") throw new Error(job.error || "Bulk payment resolution stopped unexpectedly");
      const list = await adminFetch("/api/admin/transactions");
      if (list?.status) {
        setTransactions(list.data.transactions || []);
        updateStatistics(list.data.stats || null, list.data.stats_error);
      }
    } catch (error) {
      setBulkResolveError(error.message);
    } finally {
      setBulkResolving(false);
    }
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const data = await adminFetch("/api/admin/transactions", {
          method: "GET"
        });

        if (data?.status) {
          setTransactions(data.data.transactions || []);
          updateStatistics(data.data.stats || null, data.data.stats_error);
        } else {
          setStats(null);
          setStatisticsError(data?.message || 'Unable to load transaction statistics. Refresh to retry.');
        }
      } catch (error) {
        setStats(null);
        setStatisticsError('Unable to load transaction statistics. Refresh to retry.');
      }

      setLoading(false);
    };

    fetchTransactions();
  }, [updateStatistics]);

  useEffect(() => {
    if (!canViewDrawSummary) {
      setSummaryLoading(false);
      return;
    }

    const fetchDrawSummary = async () => {
      setSummaryLoading(true);

      try {
        const data = await adminFetch("/api/admin/transactions/draw-summary", {
          method: "GET"
        });

        if (data?.status) {
          setDrawSummaries(data.data.summaries || []);
        }
      } catch (error) {
        console.log(error);
      }

      setSummaryLoading(false);
    };

    fetchDrawSummary();
  }, [canViewDrawSummary]);

  useEffect(() => {
    const term = search.trim();
    if (!term) { setReferenceMatches([]); return undefined; }
    let cancelled = false;
    const timer = setTimeout(() => {
      adminFetch(`/api/admin/transactions?search_reference=${encodeURIComponent(term)}`)
        .then((response) => { if (!cancelled && response?.status) setReferenceMatches(response.data.transactions || []); })
        .catch(() => { if (!cancelled) setReferenceMatches([]); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [search]);

  useEffect(() => {
    const normalizedSearch = search.toLowerCase();

    const mergedTransactions = [...transactions];
    const knownIds = new Set(transactions.map((transaction) => transaction.id));
    for (const payment of referenceMatches) {
      if (!knownIds.has(payment.id)) mergedTransactions.push(payment);
    }
    const nextTransactions = mergedTransactions.filter((transaction) => {
      const matchesSearch =
        transaction.user_name.toLowerCase().includes(normalizedSearch) ||
        String(transaction.email || "").toLowerCase().includes(normalizedSearch) ||
        transaction.reference.toLowerCase().includes(normalizedSearch) ||
        transaction.source.toLowerCase().includes(normalizedSearch);

      const matchesStatus =
        statusFilter === "all" || transaction.status === statusFilter;

      const matchesRecordType =
        recordTypeFilter === "all" || transaction.record_type === recordTypeFilter;

      return matchesSearch && matchesStatus && matchesRecordType;
    });

    setFilteredTransactions(nextTransactions);
  }, [transactions, referenceMatches, search, statusFilter, recordTypeFilter]);

  useEffect(() => {
    setTransactionPage(1);
  }, [search, statusFilter, recordTypeFilter]);

  useEffect(() => {
    if (activeTab === "transactions") {
      setTransactionPage(1);
      return;
    }

    setSummaryPage(1);
  }, [activeTab]);

  const cards = [
    {
      label: "Total Transactions",
      value: loading ? 'Loading…' : stats?.totalTransactions ?? 'Unavailable',
      icon: ReceiptIcon
    },
    {
      label: "Successful",
      value: loading ? 'Loading…' : stats?.successfulTransactions ?? 'Unavailable',
      icon: CheckCircleIcon
    },
    {
      label: "Pending",
      value: loading ? 'Loading…' : stats?.pendingTransactions ?? 'Unavailable',
      icon: PendingActionsIcon
    },
    {
      label: "Total Volume",
      value: loading ? 'Loading…' : formatTransactionNaira(stats?.totalVolume),
      icon: PaidIcon
    }
  ];

  const visibleCards = isSuperAdmin ? cards : [];
  const transactionTotalPages = Math.ceil(filteredTransactions.length / rowsPerPage) || 1;
  const paginatedTransactions = filteredTransactions.slice(
    (transactionPage - 1) * rowsPerPage,
    transactionPage * rowsPerPage
  );
  const summaryTotalPages = Math.ceil(drawSummaries.length / rowsPerPage) || 1;
  const paginatedDrawSummaries = drawSummaries.slice(
    (summaryPage - 1) * rowsPerPage,
    summaryPage * rowsPerPage
  );

  const getStatusChip = (status) => {
    switch (status) {
      case "success":
        return <Chip label="Success" sx={{ bgcolor: Colors.success, color: "#fff" }} />;
      case "pending":
        return <Chip label="Pending" sx={{ bgcolor: "#ed6c02", color: "#fff" }} />;
      case "failed":
        return <Chip label="UNSUCCESSFUL" sx={{ bgcolor: Colors.error, color: "#fff" }} />;
      case "closed":
        return <Chip label="CLOSED" sx={{ bgcolor: "#b42318", color: "#fff" }} />;
      default:
        return <Chip label={status} />;
    }
  };

  const getRecordTypeChip = (recordType) => {
    switch (recordType) {
      case "payment":
        return <Chip label="Payment" sx={{ bgcolor: "#1976d2", color: "#fff" }} />;
      case "wallet_transaction":
        return <Chip label="Wallet" sx={{ bgcolor: "#6a1b9a", color: "#fff" }} />;
      default:
        return <Chip label={recordType} />;
    }
  };

  const transactionColumns = [
    {
      key: "index",
      label: "S/N",
      render: (_transaction, index) => index + 1,
      hideOnMobile: true,
      headCellSx: { ...transactionHeadCellSx, minWidth: 64 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 64 }
    },
    {
      key: "user_name",
      label: "User",
      render: (transaction) => transaction.user_name,
      headCellSx: { ...transactionHeadCellSx, minWidth: 170 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 170 }
    },
    {
      key: "email",
      label: "Email",
      render: (transaction) => <Tooltip title={transaction.email || ""} arrow>
        <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{transaction.email || "-"}</Typography>
      </Tooltip>,
      mobileRender: (transaction) => <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {transaction.email || "-"}
      </Typography>,
      headCellSx: { ...transactionHeadCellSx, minWidth: 220 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 220, maxWidth: 260 }
    },
    {
      key: "record_type",
      label: "Record",
      render: (transaction) => getRecordTypeChip(transaction.record_type),
      headCellSx: { ...transactionHeadCellSx, minWidth: 110 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 110 }
    },
    {
      key: "transaction_type",
      label: "Type",
      render: (transaction) => transaction.transaction_type,
      headCellSx: { ...transactionHeadCellSx, minWidth: 150 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 150 }
    },
    {
      key: "source",
      label: "Source",
      render: (transaction) => transaction.source,
      mobileLabel: "Funding Source",
      headCellSx: { ...transactionHeadCellSx, minWidth: 110 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 110 }
    },
    {
      key: "amount",
      label: "Amount",
      render: (transaction) => `₦${Number(transaction.amount || 0).toLocaleString()}`,
      headCellSx: { ...transactionHeadCellSx, minWidth: 100 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 100 }
    },
    {
      key: "status",
      label: "Status",
      render: (transaction) => <Stack spacing={0.5} alignItems="flex-start">
        {getStatusChip(transaction.status)}
        {transaction.status === "failed" && transaction.resolution_provider_status &&
          <Typography variant="caption" color="error.main" sx={{ textTransform: "capitalize" }}>
            {transaction.resolution_provider_status}
          </Typography>}
      </Stack>,
      headCellSx: { ...transactionHeadCellSx, minWidth: 140 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 140 }
    },
    {
      key: "fulfillment_status",
      label: "Fulfillment",
      render: (transaction) => transaction.record_type === "payment"
        ? <Chip size="small" label={!transaction.reconciliation_eligible ? "Historical / Not Eligible"
          : transaction.status === "closed" ? "Closed / Not Fulfilled"
          : transaction.manual_confirmation ? "MANUALLY CONFIRMED / FULFILLED"
          : transaction.status === "failed" ? "Payment Failed"
            : transaction.reconciled_at ? "Reconciled"
              : transaction.fulfillment_status === "FULFILLED" ? "Successful / Fulfilled"
                : transaction.fulfillment_status === "PARTIALLY_FULFILLED" ? "Partially Fulfilled"
                  : transaction.status === "success" && transaction.fulfillment_status === "NOT_FULFILLED" ? "Reconciliation Required"
                  : transaction.status === "success" ? "Payment Confirmed - Fulfilment Pending"
                    : "Payment Pending"}
            color={transaction.status === "closed" ? "error" : transaction.fulfillment_status === "FULFILLED" ? "success" : "warning"} />
        : "-",
      headCellSx: { ...transactionHeadCellSx, minWidth: 220 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 220 }
    },
    {
      key: "reference",
      label: "Reference",
      render: (transaction) => <Tooltip title={transaction.reference || ""} arrow>
        <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>{transaction.reference}</Typography>
      </Tooltip>,
      mobileRender: (transaction) => <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {transaction.reference}
      </Typography>,
      headCellSx: { ...transactionHeadCellSx, minWidth: 240 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 240, maxWidth: 280 }
    },
    {
      key: "resolve",
      label: "Resolve",
      isActions: true,
      render: (transaction) => isSuperAdmin && transaction.record_type === "payment" &&
        transaction.status === "pending" && transaction.reconciliation_eligible
        ? <Button size="small" variant="contained" onClick={(event) => {
          event.stopPropagation(); resolvePendingPayment(transaction);
        }} disabled={Boolean(resolvingPaymentId)}>
          {resolvingPaymentId === transaction.record_id ? "Checking Paystack..." : "Resolve Payment"}
        </Button>
        : null,
      headCellSx: { ...transactionHeadCellSx, minWidth: 170 },
      desktopCellSx: {
        ...noWrapDesktopCellSx,
        minWidth: 170,
        "& .MuiButton-root": { whiteSpace: "nowrap", minWidth: 142 }
      }
    },
    {
      key: "actions",
      label: "Message",
      isActions: true,
      render: (transaction) => transaction.record_type === "payment" && transaction.user_id
        ? <Button size="small" startIcon={<MailOutlineIcon />} onClick={(event) => {
          event.stopPropagation(); navigate(`/admin/messages?paymentId=${transaction.record_id}`);
        }} aria-label={`Email customer about ${transaction.reference}`}>Email Customer</Button>
        : null,
      headCellSx: { ...transactionHeadCellSx, minWidth: 120 },
      desktopCellSx: {
        ...noWrapDesktopCellSx,
        minWidth: 120,
        "& .MuiButton-root": { whiteSpace: "nowrap", minWidth: 100 }
      }
    },
    {
      key: "created_at",
      label: "Date",
      render: (transaction) =>
        formatAdminDateTime(transaction.created_at, { fallback: "-" }),
      headCellSx: { ...transactionHeadCellSx, minWidth: 190 },
      desktopCellSx: { ...noWrapDesktopCellSx, minWidth: 190 }
    }
  ];

  const drawSummaryColumns = [
    {
      key: "draw_mode",
      label: "Mode",
      render: (summary) => <Chip size="small" label={summary.draw_mode === "test" ? "TEST MODE" : "STANDARD"}
        color={summary.draw_mode === "test" ? "warning" : "default"} />
    },
    {
      key: "draw_ref_no",
      label: "Draw Ref No.",
      render: (summary) => summary.draw_ref_no
    },
    {
      key: "total_tickets",
      label: "Total Number of Tickets",
      render: (summary) => Number(summary.total_tickets || 0).toLocaleString()
    },
    {
      key: "eligible_tickets",
      label: "Eligible Tickets",
      render: (summary) => Number(summary.eligible_tickets || 0).toLocaleString()
    },
    {
      key: "total_credit",
      label: "Total Credit",
      render: (summary) => `₦${Number(summary.total_credit || 0).toLocaleString()}`
    },
    {
      key: "total_debit",
      label: "Total Debit",
      render: (summary) => `₦${Number(summary.total_debit || 0).toLocaleString()}`
    },
    {
      key: "status",
      label: "Status",
      render: (summary) => getStatusChip(summary.status)
    },
    {
      key: "date",
      label: "Date",
      render: (summary) => formatAdminDateTime(summary.date, { fallback: "-" })
    }
  ];

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <AdminPageContainer>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
          Transactions
        </Typography>

        {canViewDrawSummary && (
          <Card sx={{ p: 1, mb: 2, borderRadius: 3, ...contentCardSx }}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="transactions" label="Transactions" />
              <Tab value="summary" label="Transaction Summary" />
            </Tabs>
          </Card>
        )}

        {statisticsError && <Alert severity="error" sx={{ mb: 2 }}>{statisticsError}</Alert>}
        {isSuperAdmin && activeTab === "transactions" && (
          <Box sx={{ ...metricGridSx, mb: 3 }}>
            {visibleCards.map((card) => (
              <Card key={card.label} sx={metricCardSx}>
                <card.icon sx={{ fontSize: 36, color: Colors.accent, mb: 1 }} />
                <Typography variant="body2">{card.label}</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {card.value}
                </Typography>
              </Card>
            ))}
          </Box>
        )}

        {isSuperAdmin && activeTab === "transactions" && stats && <Typography variant="body2" sx={{ mb: 2 }}>
          All-time business activity totals. Search and filters apply to the recent transaction list, not these totals.
        </Typography>}
        {activeTab === "transactions" && (
          <>
            {resolutionMessage && <Alert severity="info" sx={{ mb: 2 }}>{resolutionMessage}</Alert>}
            {isSuperAdmin && (
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" sx={{ mb: 2 }}>
                <Button variant="contained" color="warning" disabled={bulkResolving}
                  onClick={() => { setBulkResolveOpen(true); setBulkResolveError(""); setBulkResolveResult(null); }}>
                  {bulkResolving ? "Resolving Payments..." : "Resolve Payments"}
                </Button>
              </Stack>
            )}
            <Card sx={{ p: { xs: 2, sm: 2.5 }, mb: 2, borderRadius: 3, ...contentCardSx }}>
              <Box sx={filterStackSx}>
                <TextField
                  placeholder="Search name, email, reference or source"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="small"
                  fullWidth
                  sx={transactionFieldSx}
                />

                <TextField
                  select
                  size="small"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  fullWidth
                  sx={transactionFieldSx}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  value={recordTypeFilter}
                  onChange={(e) => setRecordTypeFilter(e.target.value)}
                  fullWidth
                  sx={transactionFieldSx}
                >
                  <MenuItem value="all">All Records</MenuItem>
                  <MenuItem value="payment">Payments</MenuItem>
                  <MenuItem value="wallet_transaction">Wallet Entries</MenuItem>
                </TextField>
              </Box>
            </Card>

            <Card sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, ...contentCardSx }}>
              <Box sx={{ position: "relative", minHeight: 420, width: "100%", maxWidth: "100%", minWidth: 0 }}>
                <ResponsiveDataTable
                  columns={transactionColumns}
                  rows={paginatedTransactions}
                  loading={loading}
                  minWidth={2000}
                  tableWrapperSx={{
                    "& th, & td": {
                      wordBreak: "normal",
                      overflowWrap: "normal",
                      verticalAlign: "middle"
                    },
                    "& .MuiChip-root": { whiteSpace: "nowrap" },
                    "& .MuiButton-root": { whiteSpace: "nowrap" }
                  }}
                  emptyMinHeight={240}
                  emptyMessage="No transactions found"
                  mobileTitle={(transaction) => transaction.user_name}
                  onRowClick={isSuperAdmin ? openPaymentDetail : undefined}
                />

                <TableLoadingOverlay loading={loading} />
              </Box>

              {!loading && filteredTransactions.length > rowsPerPage ? (
                <Stack sx={paginationBarSx}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Page {transactionPage} of {transactionTotalPages}
                  </Typography>
                  <Pagination
                    count={transactionTotalPages}
                    page={transactionPage}
                    onChange={(_event, value) => setTransactionPage(value)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                    sx={{
                      "& .MuiPaginationItem-root": { color: "text.primary" },
                      "& .Mui-selected": {
                        bgcolor: `${Colors.accent} !important`,
                        color: "#003300 !important"
                      }
                    }}
                  />
                </Stack>
              ) : null}
            </Card>
          </>
        )}

        {canViewDrawSummary && activeTab === "summary" && (
          <Card sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, ...contentCardSx }}>
            <Box sx={{ position: "relative", minHeight: 420, width: "100%", maxWidth: "100%", minWidth: 0 }}>
              <ResponsiveDataTable
                columns={drawSummaryColumns}
                rows={paginatedDrawSummaries}
                loading={summaryLoading}
                minWidth={900}
                emptyMinHeight={240}
                emptyMessage="No draw summaries found"
                mobileTitle={(summary) => summary.draw_ref_no}
              />

              <TableLoadingOverlay loading={summaryLoading} />
            </Box>

            {!summaryLoading && drawSummaries.length > rowsPerPage ? (
              <Stack sx={paginationBarSx}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Page {summaryPage} of {summaryTotalPages}
                </Typography>
                <Pagination
                  count={summaryTotalPages}
                  page={summaryPage}
                  onChange={(_event, value) => setSummaryPage(value)}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": { color: "text.primary" },
                    "& .Mui-selected": {
                      bgcolor: `${Colors.accent} !important`,
                      color: "#003300 !important"
                    }
                  }}
                />
              </Stack>
            ) : null}
          </Card>
        )}
      </AdminPageContainer>
      <Dialog open={Boolean(paymentDetail)} onClose={closePaymentDetail} fullWidth maxWidth="sm"
        PaperProps={{ sx: { width: { xs: "calc(100vw - 24px)", sm: "100%" }, maxHeight: "90vh" } }}>
        <DialogTitle>Payment Reconciliation</DialogTitle>
        <DialogContent dividers sx={{ overflowY: "auto" }}>
          {detailLoading ? <CircularProgress /> : paymentDetail?.reference ? <Stack spacing={1.5} sx={{ overflowWrap: "anywhere" }}>
            <Typography><strong>Customer:</strong> {paymentDetail.customer?.name || "Unknown"} ({paymentDetail.customer?.profile_id || "-"})</Typography>
            <Typography><strong>Email:</strong> {paymentDetail.customer?.email || "-"}</Typography>
            <Typography><strong>Type:</strong> {paymentDetail.type}</Typography>
            <Typography><strong>Provider:</strong> {paymentDetail.gateway}</Typography>
            <Typography><strong>Reference:</strong> {paymentDetail.reference}</Typography>
            <Typography><strong>Amount:</strong> ₦{Number(paymentDetail.amount || 0).toLocaleString()}</Typography>
            <Typography><strong>Date:</strong> {formatAdminDateTime(paymentDetail.created_at, { fallback: "-" })}</Typography>
            <Typography><strong>Local payment:</strong> {paymentDetail.payment_status}</Typography>
            <Typography><strong>Paystack:</strong> {paymentDetail.paystack_status}</Typography>
            {paymentDetail.closure && <Alert severity="error">
              <strong>CLOSED / NOT FULFILLED</strong><br />
              Closed by: {paymentDetail.closure.closed_by_name || `Admin #${paymentDetail.closure.closed_by_admin_id}`}<br />
              Closed at: {formatAdminDateTime(paymentDetail.closure.closed_at)}<br />
              Reason: {paymentDetail.closure.reason}<br />
              Provider status at closure: {paymentDetail.closure.provider_status_at_closure}
            </Alert>}
            {paymentDetail.manual_confirmation && <Alert severity="success">
              MANUALLY CONFIRMED / FULFILLED — Confirmed by Admin #{paymentDetail.manual_confirmation.confirmed_by} at {formatAdminDateTime(paymentDetail.manual_confirmation.confirmed_at)}. Reason: {paymentDetail.manual_confirmation.reason}. Provider status at override: {paymentDetail.manual_confirmation.provider_status}.
            </Alert>}
            <Typography><strong>Fulfillment:</strong> {String(paymentDetail.fulfillment_status || "UNKNOWN").replaceAll("_", " ")}</Typography>
            {paymentDetail.draw && <Typography><strong>Draw:</strong> {paymentDetail.draw.draw_code} ({paymentDetail.draw.status})</Typography>}
            {paymentDetail.type === "ticket_purchase" && <>
              <Typography><strong>Original ticket price:</strong> {paymentDetail.unit_price == null ? "Unknown" : `₦${Number(paymentDetail.unit_price).toLocaleString()}`}</Typography>
              <Typography><strong>Expected / Issued / Missing:</strong> {paymentDetail.expected_quantity ?? "Unknown"} / {paymentDetail.issued_quantity ?? "Unknown"} / {paymentDetail.missing_quantity ?? "Unknown"}</Typography>
            </>}
            {paymentDetail.review_reason && <Alert severity="warning">Manual review required: {paymentDetail.review_reason.replaceAll("_", " ")}. No tickets will be issued automatically.</Alert>}
            <Alert severity={paymentDetail.paystack_status === "success" ? "info" : "warning"}>
              <strong>Proposed action:</strong> {paymentDetail.proposed_action}
            </Alert>
            {paymentDetail.paystack_status !== "success" && <Alert severity="warning">Provider verification: Not confirmed by Paystack</Alert>}
            {paymentDetail.resolution_status !== "closed" && paymentDetail.paystack_status === "success" && paymentDetail.fulfillment_status !== "FULFILLED" && !paymentDetail.review_reason &&
              <TextField type="password" label="Your admin password" value={adminPassword}
                onChange={(event) => setAdminPassword(event.target.value)} fullWidth autoComplete="current-password" />}
          </Stack> : null}
          {detailError && <Alert severity={paymentDetail?.fulfillment_status === "FULFILLED" ? "success" : "error"} sx={{ mt: 2 }}>{detailError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 1, p: 2 }}>
          <Button onClick={closePaymentDetail} disabled={reconciling} sx={{ width: { xs: "100%", sm: "auto" } }}>Close</Button>
          <Button variant="contained" onClick={reconcilePayment} disabled={detailLoading || reconciling || !adminPassword ||
            paymentDetail?.resolution_status === "closed" || paymentDetail?.paystack_status !== "success" || paymentDetail?.fulfillment_status === "FULFILLED" || Boolean(paymentDetail?.review_reason)}
            sx={{ width: { xs: "100%", sm: "auto" } }}>{reconciling ? "Reconciling..." : "Confirm & Reconcile"}</Button>
          {isSuperAdmin && paymentDetail?.manual_override_eligible &&
            <Button color="warning" variant="outlined" onClick={() => { setManualOpen(true); setDetailError(""); }}
              disabled={reconciling || detailLoading}>Manually Confirm Payment</Button>}
          {isSuperAdmin && paymentDetail?.closure_eligible &&
            <Button color="error" variant="contained" onClick={() => { setClosureOpen(true); setDetailError(""); }}
              disabled={reconciling || detailLoading}>Close Transaction</Button>}
        </DialogActions>
      </Dialog>
      <Dialog open={bulkResolveOpen} onClose={closeBulkResolveDialog} fullWidth maxWidth="md"
        PaperProps={{ sx: { width: { xs: "calc(100vw - 24px)", sm: "100%" }, maxHeight: "90vh" } }}>
        <DialogTitle>Resolve All Eligible Pending Payments</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {!bulkResolveResult && !bulkResolving && <>
              <Alert severity="warning">
                Resolve all eligible pending Paystack payments? Each transaction will be independently verified with Paystack.
                Successful payments will be fulfilled, failed or abandoned payments will be closed, and payments still pending
                at Paystack will remain pending.
              </Alert>
              <Typography variant="body2">
                This checks every eligible forward-only payment on the server, not only the transactions visible on this page.
              </Typography>
              <TextField type="password" label="Current Super Admin password" value={bulkResolvePassword}
                onChange={(event) => setBulkResolvePassword(event.target.value)} autoComplete="current-password" fullWidth />
              <FormControlLabel control={<Checkbox checked={bulkResolveConfirmed}
                onChange={(event) => setBulkResolveConfirmed(event.target.checked)} />}
                label="I confirm that each payment must be independently verified with Paystack before any action is taken." />
            </>}

            {bulkResolving && <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={22} />
              <Typography>Verifying and resolving eligible payments...</Typography>
            </Stack>}
            {bulkResolveError && <Alert severity="error">{bulkResolveError}</Alert>}

            {bulkResolveResult && <>
              <Alert severity={bulkResolveResult.status === "completed" ? "success" : "info"}>
                {bulkResolveResult.status === "completed"
                  ? "Bulk payment resolution completed. The Transactions table has been refreshed."
                  : `Bulk payment resolution is running (${bulkResolveResult.summary?.processed || 0} processed).`}
              </Alert>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1 }}>
                {[
                  ["Eligible payments", "eligible"], ["Total checked", "processed"],
                  ["Successful / Fulfilled", "successful_fulfilled"], ["Failed", "failed"],
                  ["Abandoned", "abandoned"], ["Still Pending", "still_pending"],
                  ["Already Resolved", "already_resolved"], ["Reference Not Found", "reference_not_found"],
                  ["Verification Errors", "verification_errors"], ["Unknown Status", "unknown_status"]
                ].map(([label, key]) => <Card key={key} variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="h6">{bulkResolveResult.summary?.[key] || 0}</Typography>
                </Card>)}
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>Per-payment results</Typography>
              <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                <Stack spacing={1}>
                  {(bulkResolveResult.results || []).map((item) => <Card key={`${item.payment_id}-${item.reference}`}
                    variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="body2" fontWeight={700}>{item.reference || `Payment #${item.payment_id}`}</Typography>
                    <Typography variant="caption" display="block">Customer: {item.customer || "Unknown"}</Typography>
                    <Typography variant="caption" display="block">
                      Previous: {item.previous_status || "-"} | Paystack: {item.paystack_status || "-"} | Final: {item.final_status || "-"}
                    </Typography>
                    <Chip size="small" label={String(item.result || "UNKNOWN").replaceAll("_", " ")} sx={{ mt: 0.75 }} />
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{item.message}</Typography>
                  </Card>)}
                  {!bulkResolveResult.results?.length && <Typography color="text.secondary">No eligible pending payments were found.</Typography>}
                </Stack>
              </Box>
            </>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkResolveDialog} disabled={bulkResolving}>
            {bulkResolveResult ? "Close" : "Cancel"}
          </Button>
          {!bulkResolveResult && <Button variant="contained" color="warning" onClick={resolveAllPendingPayments}
            disabled={bulkResolving || !bulkResolvePassword || !bulkResolveConfirmed}>
            {bulkResolving ? "Resolving..." : "Resolve Payments"}
          </Button>}
        </DialogActions>
      </Dialog>
      <Dialog open={closureOpen} onClose={closeClosureDialog} fullWidth maxWidth="sm">
        <DialogTitle>Close Transaction</DialogTitle>
        <DialogContent dividers><Stack spacing={1.5}>
          <Typography><strong>Customer:</strong> {paymentDetail?.customer?.name} ({paymentDetail?.customer?.profile_id})</Typography>
          <Typography><strong>Email:</strong> {paymentDetail?.customer?.email}</Typography>
          <Typography><strong>Type / Amount:</strong> {paymentDetail?.type} / ₦{Number(paymentDetail?.amount || 0).toLocaleString()}</Typography>
          <Typography><strong>Reference:</strong> {paymentDetail?.reference}</Typography>
          <Typography><strong>Date:</strong> {formatAdminDateTime(paymentDetail?.created_at, { fallback: "-" })}</Typography>
          <Typography><strong>Local / Paystack:</strong> {paymentDetail?.payment_status} / {paymentDetail?.paystack_status}</Typography>
          <Typography><strong>Fulfillment:</strong> {paymentDetail?.fulfillment_status}</Typography>
          <Alert severity="error">Closing this transaction will mark it as administratively resolved and prevent payment fulfillment through the Admin reconciliation workflow. Confirm that payment was not received before continuing.</Alert>
          <TextField label="Closure Reason *" multiline minRows={3} inputProps={{ maxLength: 150 }}
            value={closureReason} onChange={(event) => setClosureReason(event.target.value)} fullWidth />
          <TextField type="password" label="Current Super Admin password" autoComplete="current-password"
            value={closurePassword} onChange={(event) => setClosurePassword(event.target.value)} fullWidth />
          <FormControlLabel control={<Checkbox checked={closureConfirmed} onChange={(event) => setClosureConfirmed(event.target.checked)} />}
            label="I confirm payment was not received and want to close this transaction." />
          {detailError && <Alert severity="error">{detailError}</Alert>}
        </Stack></DialogContent>
        <DialogActions><Button onClick={closeClosureDialog} disabled={reconciling}>Cancel</Button>
          <Button color="error" variant="contained" onClick={closeTransaction}
            disabled={reconciling || !closurePassword || closureReason.trim().length < 10 || !closureConfirmed || !paymentDetail?.closure_eligible}>
            {reconciling ? "Closing..." : "CLOSE TRANSACTION"}
          </Button></DialogActions>
      </Dialog>
      <Dialog open={manualOpen} onClose={() => { if (!reconciling) setManualOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>High-Risk Manual Payment Confirmation</DialogTitle>
        <DialogContent dividers><Stack spacing={1.5}>
          <Typography><strong>Customer:</strong> {paymentDetail?.customer?.name} ({paymentDetail?.customer?.profile_id})</Typography>
          <Typography><strong>Email:</strong> {paymentDetail?.customer?.email}</Typography>
          <Typography><strong>Reference:</strong> {paymentDetail?.reference}</Typography>
          <Typography><strong>Type / Amount:</strong> {paymentDetail?.type} / ₦{Number(paymentDetail?.amount || 0).toLocaleString()}</Typography>
          <Typography><strong>Provider / Status:</strong> {paymentDetail?.gateway} / {paymentDetail?.paystack_status}</Typography>
          <Typography><strong>Local / Fulfillment:</strong> {paymentDetail?.payment_status} / {paymentDetail?.fulfillment_status}</Typography>
          {paymentDetail?.type === "ticket_purchase" ? <>
            <Typography><strong>Draw:</strong> {paymentDetail?.draw?.draw_code}</Typography>
            <Typography><strong>Original quantity / price:</strong> {paymentDetail?.expected_quantity} / ₦{Number(paymentDetail?.unit_price || 0).toLocaleString()}</Typography>
            <Typography><strong>Expected / Issued / Missing:</strong> {paymentDetail?.expected_quantity} / {paymentDetail?.issued_quantity} / {paymentDetail?.missing_quantity}</Typography>
          </> : <Typography><strong>Expected / Already credited / Remaining:</strong> ₦{Number(paymentDetail?.wallet_expected || 0).toLocaleString()} / {paymentDetail?.wallet_credited ? "Yes" : "No"} / ₦{paymentDetail?.wallet_credited ? 0 : Number(paymentDetail?.wallet_expected || 0).toLocaleString()}</Typography>}
          <Alert severity="error">Paystack has NOT confirmed this payment. Continue only if you have independently verified that FlipLottery received the money.</Alert>
          <TextField type="password" label="Current Super Admin password" value={manualPassword} onChange={(event) => setManualPassword(event.target.value)} autoComplete="current-password" fullWidth />
          <TextField label="Mandatory reason" value={manualReason} onChange={(event) => setManualReason(event.target.value)} inputProps={{ maxLength: 500 }} multiline minRows={2} fullWidth />
          <FormControlLabel control={<Checkbox checked={manualConfirmed} onChange={(event) => setManualConfirmed(event.target.checked)} />} label="I confirm FlipLottery received this payment." />
          {detailError && <Alert severity="error">{detailError}</Alert>}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setManualOpen(false)} disabled={reconciling}>Cancel</Button>
          <Button color="error" variant="contained" onClick={manuallyConfirmPayment}
            disabled={reconciling || !manualPassword || manualReason.trim().length < 10 || !manualConfirmed || !paymentDetail?.manual_override_eligible}>
            {reconciling ? "Confirming..." : "Confirm Payment & Fulfill"}
          </Button></DialogActions>
      </Dialog>
    </Box>
  );
}
